import {
  useContext,
  useEffect,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  MenuItem,
  Select,
} from "@mui/material";
import { Tooltip } from "@mui/material"; // For missing entries
import { ActiveIcon, InactiveIcon } from "../../../assets/svg/icons";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import API_URLS, { BASE_URL } from "../../../constants/apiConstants";
import AuthContext from "../../../context/AuthProvider";
import { FaUserCircle } from "react-icons/fa";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CustomMonthYearPicker from "../../../components/components/js/monthPicker";
import { AttendanceSearchComponent } from "../../../components/components/js/AttendanceSearchComponent";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { LoadingDots } from "../../../components/general";

export const StudentAttendance = () => {
  const axiosPrivate = useAxiosPrivate();
  const { school, auth } = useContext(AuthContext);
  const { t } = useTranslation();
  const [error, setError] = useState(null);
  const [students, setStudents] = useState();
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [days, setDays] = useState([]);
  const [loading, setLoading] = useState(true);
  const today = new Date();
  const [selectedMonth, setSelectedMonth] = useState(today.getMonth());
  const [selectedYear, setSelectedYear] = useState(today.getFullYear());
  const [selectedRoom, setSelectedRoom] = useState("general");
  const [searchParams, setSearchParams] = useSearchParams();

  const { i18n } = useTranslation();
  const isRTL = i18n.dir() === "rtl";

  const handleRoomChange = (event) => {
    const roomId = event.target.value;
    setSelectedRoom(roomId);
    setSearchParams({ room: roomId });
  };

  useEffect(() => {
    const fetchRooms = async () => {
      const url = API_URLS.getRooms(auth.id, school.id);
      const roomsRes = await axiosPrivate.get(url);
      // V2 classrooms endpoint is paginated ({ results: [...] }), not a
      // flat array — this is what was throwing "data.map is not a
      // function". Field is `name`, not the old V1 `ClassName`.
      const results = roomsRes.data?.results || [];
      const allRooms = results.map((room) => ({
        id: room.id,
        room: room.name,
      }));
      setRooms(allRooms);
    };

    fetchRooms();

  }, [auth.id, school.id, axiosPrivate]);
  
  useEffect(() => {
    const fetchAttendance = async () => {
      try {
        setLoading(true);
        setError(null);
        // Zero-padded YYYY-MM to match the format used everywhere else in
        // the app (getStudentMonthlyAttendance doesn't confirm the backend
        // actually needs this, but "2026-8" vs "2026-08" is worth being
        // consistent about regardless).
        const date = `${selectedYear}-${String(selectedMonth + 1).padStart(2, "0")}`;
        const roomId = selectedRoom !== "general" ? selectedRoom : "";
        // V2: was API_URLS.getStudentsAttendance (V1 `/api/nursery/checkin_set/...`,
        // now 404s). NOTE: `month` and `room_id` filtering are unconfirmed
        // against a live backend response — see apiConstants.js comment on
        // getStudentMonthlyAttendance. If the backend ignores either, the
        // month picker / room dropdown here won't actually narrow results.
        const url = API_URLS.getStudentMonthlyAttendance(school.id, date, roomId);
        const attendanceRes = await axiosPrivate.get(url);

        setStudents(attendanceRes.data.students);
        setFilteredStudents(attendanceRes.data.students);
        setDays(
          Array.from({ length: attendanceRes.data.last_day }, (_, i) =>
            (i + 1).toString().padStart(2, "0")
          )
        );
      } catch (error) {
        console.error("GET request error:", error);
        toast.error(t("attendance.loadError"));
        setError(t("attendance.loadError"));
        setStudents([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAttendance();
  }, [axiosPrivate, school.id, selectedMonth, selectedRoom, selectedYear, t]);

  // Effect to set selected room when the params changed
  useEffect(() => {
    if (rooms?.length){
      const roomParam = searchParams.get('room');
      const roomId = roomParam !== null && /^\d+$/.test(roomParam) ? Number(roomParam) : null;
      if (roomId) {
        setSelectedRoom(roomId);
      }
    }
  }, [searchParams, rooms]);

  const handleSearch = (value) => {
    if (!value) return setFilteredStudents(students);

    const query = value.toLowerCase();
    const results = students.filter((s) =>
      s.name.toLowerCase().includes(query)
    );
    setFilteredStudents(results);
  };

  return (
    <Box
      elevation={15}
      sx={{ marginTop: "2rem", padding: "0 1rem" }}
    >
      <Box display="flex" justifyContent="space-between" pb={2}>
        <Typography variant="h6" gutterBottom>
          {t("admin_titles.attendance")}
        </Typography>
        <Box display="flex" gap={8}>
          <AttendanceSearchComponent onSearch={handleSearch} />

          <Box display="flex" gap={2}>
            <CustomMonthYearPicker
              selectedMonth={selectedMonth}
              setSelectedMonth={setSelectedMonth}
              selectedYear={selectedYear}
              setSelectedYear={setSelectedYear}
            />
            <Box
              sx={{
                display: "flex",
                alignSelf: "center",
                gap: 1,
                backgroundColor: "#f7f5fa",
                borderRadius: "8px",
                padding: "4px 8px",
                maxHeight: "2.2rem",
              }}
            >
              <Select
                value={selectedRoom}
                variant="standard"
                disableUnderline
                IconComponent={ArrowDropDownIcon}
                onChange={handleRoomChange}
                sx={{
                  color: "gray",
                //   fontWeight: 500,
                  background: "transparent",
                  minWidth: 50,
                fontSize: "14px",

                  "& .MuiSelect-icon": {
                    position: "absolute",
                    top: "50%",
                    transform: "translateY(-50%)",
                    ...(isRTL
                      ? { left: 4, right: "auto" }
                      : { right: 4, left: "auto" }),
                  },
                  ...(isRTL ? { pl: 4 } : { pr: 4 }),
                }}
              >
                <MenuItem value="general">{t("attendance.general")}</MenuItem>
                {rooms.map((room) => (
                  <MenuItem key={room.id} value={room.id}>
                    {t("attendance.room")} {room.room}
                  </MenuItem>
                ))}
              </Select>
            </Box>
          </Box>
        </Box>
      </Box>
      {loading ? (
        <LoadingDots />
      ) : error ? (
        <div className="d-flex justify-content-center align-items-center vh-100 fw-bold fs-1 text-danger">
          {error}
        </div>
      ) : filteredStudents.length > 0 ? (
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            borderRadius: "8px",
            maxWidth: "100%",
            overflowX: "auto",
            border: "1px solid #ccc",
          }}
        >
          <Table
            sx={{
              width: "100%",
              tableLayout: "auto", // let browser size columns based on content
              "& th, & td": {
                whiteSpace: "normal", // allow text wrap inside cells
                wordBreak: "break-word", // break long words if needed
                minWidth: 35, // keep columns readable
                padding: "6px 8px",
                fontSize: "0.875rem",
              },
              "& th": {
                backgroundColor: "#f3f3f3",
                position: "sticky",
                top: 0,
                zIndex: 2,
              },
              "& th:first-of-type": {
                position: "sticky",
                left: 0,
                textAlign: isRTL ? "right" : "left",
                backgroundColor: "#f3f3f3",
                zIndex: 3,
                minWidth: 130, // wider for names
              },
              "& td:first-of-type": {
                position: "sticky",
                right: 0,
                backgroundColor: "white",
                zIndex: 1,
                minWidth: 130,
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell>
                  <b>{t("attendance.name")}</b>
                </TableCell>
                {days.map((day) => (
                  <TableCell key={day} align="center">
                    {day}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredStudents.map((student, idx) => (
                <TableRow key={idx}>
                  <TableCell>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      {student.img ? (
                        <img
                          src={BASE_URL + student.img}
                          alt={student.name}
                          style={{
                            width: "24px",
                            height: "24px",
                            borderRadius: "50%",
                          }}
                        />
                      ) : (
                        <FaUserCircle size={24} />
                      )}
                      <span style={{ fontSize: "11px" }}>{student.name}</span>
                    </div>
                  </TableCell>
                  {days.map((day) => {
                    const attendanceForDay = student.attendance.find(
                      (a) => a.date === day
                    );

                    let statusIcon = null;
                    if (!attendanceForDay) {
                      statusIcon = <span>-</span>;
                    } else if (attendanceForDay.status === "Present") {
                      // NOTE: the live V2 sample I confirmed only had "Absent"
                      // days, so `first_checkin`/`last_checkout` on a
                      // "Present" day entry are UNCONFIRMED field names —
                      // verify against a real Present day before trusting
                      // the tooltip values.
                      const checkIn = attendanceForDay.first_checkin || "N/A";
                      const checkOut = attendanceForDay.last_checkout || "N/A";

                      statusIcon = (
                        <Tooltip
                          title={
                            <div>
                              <div>
                                <b>Check-in:</b> {checkIn}
                              </div>
                              <div>
                                <b>Check-out:</b> {checkOut}
                              </div>
                            </div>
                          }
                          arrow
                        >
                          <span>
                            <ActiveIcon />
                          </span>
                        </Tooltip>
                      );
                    } else if (attendanceForDay.status === "Absent") {
                      statusIcon = <InactiveIcon />;
                    } else {
                      statusIcon = <span>-</span>;
                    }

                    return (
                      <TableCell key={day} align="center">
                        {statusIcon}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <div className="d-flex justify-content-center align-items-center fw-bold fs-1" style={{ minHeight: '500px' }}>
          {t("attendance.noData")}
        </div>
      )}
    </Box>
  );
};